import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { atom, useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { BasicDialog } from '../../../../components/Dialog/BasicDialog';
import { FormFieldsContainer } from '../../../../components/Form/FormComponents';
import { FormFactoryWithStandardLayout } from '../../../../components/Form/FormFactory';
import { StickyFormButtons } from '../../../../components/Form/StickyFormButtons';
import { createCompanyDataModel, ICompanyDataModel } from '../../../../data-models/company.data-model';
import { ICompanyViewModel } from '../../../../view-models/company.view-model';
import { createFormFromFieldsArray } from '../../../../view-models/form.view-model';
import {
  fromCompanyDataModel,
  IDigestPanelViewModel,
} from '../../../CompanyProfiles2/Summary/DigestPanel.view-model';
import { companyFormSchema, useCompanyFields } from '../../../Finance2/Forms/CompanyFormUtils';
import { useCompanyListCustomFieldsViewModel } from '../../Companies2/CompanyDetails2';
import { useCreateListCompany } from './useCreateListCompany';

export const showCreateListCompanyDialogAtom = atom<boolean>(false);
export const newListCompanyFormDataAtom = atom<Partial<ICompanyDataModel> | null>(null);

export function CreateListCompanyModal() {
  const [showCreateListCompanyDialog, setShowCreateListCompanyDialog] = useAtom(
    showCreateListCompanyDialogAtom
  );
  const [newListCompanyFormData, setNewListCompanyFormData] = useAtom(newListCompanyFormDataAtom);

  const onClose = useCallback(() => {
    setShowCreateListCompanyDialog(false);
    setNewListCompanyFormData(null);
  }, [setNewListCompanyFormData, setShowCreateListCompanyDialog]);

  if (!showCreateListCompanyDialog) {
    return null;
  }

  return (
    <BasicDialog title='Create Company' open={showCreateListCompanyDialog} onClose={onClose}>
      <CreateListCompanyForm
        defaultValues={newListCompanyFormData ?? createCompanyDataModel()}
        onClose={onClose}
      />
    </BasicDialog>
  );
}

function CreateListCompanyForm({
  defaultValues,
  onClose,
}: {
  defaultValues: Partial<ICompanyDataModel>;
  onClose: () => void;
}) {
  const methods = useForm<Partial<IDigestPanelViewModel>>({
    defaultValues: fromCompanyDataModel(defaultValues),
    resolver: yupResolver(companyFormSchema() as ObjectSchema<Partial<ICompanyViewModel>>),
  });
  const { createListCompany, loading } = useCreateListCompany();
  const {
    trigger,
    formState: { errors },
  } = methods;
  const onSubmit = useCallback(
    async (data: Partial<IDigestPanelViewModel>) => {
      await createListCompany({ data, errors, trigger, onClose });
    },
    [createListCompany, errors, onClose, trigger]
  );

  return (
    <FormProvider {...methods}>
      <FormFieldsContainer>
        <CreateListCompanyFields />
      </FormFieldsContainer>
      <Box padding={'1rem 2.5rem 0'}>
        <StickyFormButtons
          onCancel={onClose}
          onSubmit={methods.handleSubmit(onSubmit)}
          loading={loading}
          style={{ padding: '0.5rem 0 2rem' }}
          submitLabel={'Create'}
        />
      </Box>
    </FormProvider>
  );
}

function CreateListCompanyFields() {
  const listCompanyCustomFields = useCompanyListCustomFieldsViewModel();
  const companyFields = useCompanyFields({
    customFields: 'none',
    variant: 'form',
    fields: [
      'name',
      'website',
      '_viewModel.primaryLocation',
      '_viewModel.ceoName',
      'sectorId',
      'shortDescription',
      'assignee',
    ],
  });
  const fields = useMemo(
    () => [...companyFields, ...(listCompanyCustomFields ?? [])],
    [companyFields, listCompanyCustomFields]
  );
  const form = createFormFromFieldsArray(fields);

  return <FormFactoryWithStandardLayout form={form} />;
}
